import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/contexts/AuthContext';
import { apiGet } from '@/lib/api';
import { ArrowLeft, Trash2, X } from 'lucide-react';
import { DeleteConfirmationDialog } from '@/components/DeleteConfirmationDialog';
import {
  getSelectionBundle,
  getCandidates,
  setVoters,
  addRecusal,
  removeRecusal,
  updateSelection,
  deleteSelection,
  type CandidateHappening,
} from '@/lib/selections-api';
import { TargetInput } from '@/components/selections/TargetInput';
import { RoundsTab } from '@/components/selections/RoundsTab';
import { ControlRoom } from '@/components/selections/ControlRoom';
import { ProgressRail } from '@/components/selections/ProgressRail';
import { computePhase } from '@/lib/selection-phase';
import {
  getSelectionTarget,
  resolveTargetField,
  suggestShortlistCount,
  suggestMaxPicks,
  targetLabel,
  type SelectionTarget,
} from '@/lib/target';
import type { User, Selection } from '@littlebooker/dal';

const inputClass = 'mt-1 w-full rounded-md border border-input bg-background px-3 py-2';

export default function SelectionDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isOwner = user?.role === 'owner';

  const { data: bundle, isLoading } = useQuery({
    queryKey: ['selection', id],
    queryFn: () => getSelectionBundle(id),
    enabled: !!id,
  });

  const { data: accountUsers = [] } = useQuery({
    queryKey: ['users'],
    queryFn: () => apiGet<User[]>('/api/v1/users'),
  });

  const { data: candidates = [] } = useQuery({
    queryKey: ['selection-candidates', id],
    queryFn: () => getCandidates(id),
    enabled: !!id,
  });

  const [showDelete, setShowDelete] = useState(false);
  const [mode, setMode] = useState<'run' | 'manage'>('run');
  const [manageTab, setManageTab] = useState('scope');
  const deleteMutation = useMutation({
    mutationFn: () => deleteSelection(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['selections'] });
      navigate('/selections');
    },
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['selection', id] });
    queryClient.invalidateQueries({ queryKey: ['selection-candidates', id] });
    queryClient.invalidateQueries({ queryKey: ['selection-suggest'] });
  };

  if (isLoading || !bundle) {
    return <p className="p-4">Loading…</p>;
  }

  const { selection, voters, recusals, rounds } = bundle;
  const candidatesInScope = candidates.filter((c) => !c.excluded);
  const target = getSelectionTarget(selection);
  const finalCount = resolveTargetField(candidatesInScope.length, target.final);
  const shortlistCount = resolveTargetField(candidatesInScope.length, target.shortlist);
  const suggestedMaxPicks = suggestMaxPicks(shortlistCount, voters.length);
  const targetDisplay = `${finalCount} (${targetLabel(target.final)})`;
  const isCurrentUserVoter = voters.some((v) => v.user_id === user?.id);
  const myRecusedIds = new Set(
    recusals.filter((r) => r.user_id === user?.id).map((r) => r.happening_id),
  );
  const revealedShortlistIds =
    (rounds
      .filter((r) => r.kind === 'nominate' && r.state === 'revealed')
      .sort((a, b) => b.ordinal - a.ordinal)[0]?.config as { shortlist_happening_ids?: string[] })
      ?.shortlist_happening_ids ?? [];
  const userName = (uid: string | null) =>
    accountUsers.find((u) => u.id === uid)?.name ?? uid ?? '—';
  const happeningTitle = (hid: string) =>
    candidates.find((c) => c.id === hid)?.title ?? hid;

  return (
    <div className="space-y-6">
      <div>
        <button
          className="mb-2 inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
          onClick={() => navigate('/selections')}
        >
          <ArrowLeft className="mr-1 h-4 w-4" /> Selections
        </button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">{selection.title}</h1>
            {selection.description && (
              <p className="text-muted-foreground">{selection.description}</p>
            )}
          </div>
          <div className="flex items-center gap-3">
            <span className="rounded-full border px-3 py-1 text-sm capitalize text-muted-foreground">
              {selection.state}
            </span>
            {isOwner && (
              <Button variant="outline" size="sm" onClick={() => setMode(mode === 'run' ? 'manage' : 'run')}>
                {mode === 'run' ? 'Manage setup' : 'Back to run'}
              </Button>
            )}
            {isOwner && (
              <Button
                variant="ghost"
                size="sm"
                className="text-destructive hover:text-destructive"
                onClick={() => setShowDelete(true)}
              >
                <Trash2 className="mr-1 h-4 w-4" /> Delete
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="rounded-md border bg-muted/20 p-3">
        <ProgressRail stage={computePhase(rounds).stage} />
        <p className="mt-2 text-xs text-muted-foreground">
          {candidatesInScope.length} candidates · {voters.length} voters · final target {targetDisplay}
        </p>
      </div>

      {isOwner && mode === 'manage' ? (
        <Tabs value={manageTab} onValueChange={setManageTab}>
          <TabsList>
            <TabsTrigger value="scope">Scope ({candidatesInScope.length})</TabsTrigger>
            <TabsTrigger value="voters">Voters ({voters.length})</TabsTrigger>
            <TabsTrigger value="recusals">Recusals ({recusals.length})</TabsTrigger>
            <TabsTrigger value="rounds">Rounds (advanced)</TabsTrigger>
          </TabsList>

          <TabsContent value="scope">
            <ScopeTab
              selection={selection}
              candidates={candidates}
              voterCount={voters.length}
              isOwner={isOwner}
              onChanged={invalidate}
            />
          </TabsContent>
          <TabsContent value="voters">
            <VotersTab
              id={id}
              accountUsers={accountUsers}
              currentVoterIds={voters.map((v) => v.user_id)}
              isOwner={isOwner}
              onSaved={invalidate}
            />
          </TabsContent>
          <TabsContent value="recusals">
            <RecusalsTab
              id={id}
              recusals={recusals}
              voters={voters}
              candidates={candidatesInScope}
              isOwner={isOwner}
              userName={userName}
              happeningTitle={happeningTitle}
              onChanged={invalidate}
            />
          </TabsContent>
          <TabsContent value="rounds">
            <RoundsTab
              selectionId={id}
              rounds={rounds}
              candidates={candidatesInScope}
              recusedIds={myRecusedIds}
              isOwner={isOwner}
              isCurrentUserVoter={isCurrentUserVoter}
              suggestedMaxPicks={suggestedMaxPicks}
              targetShortlist={shortlistCount}
              targetFinal={finalCount}
              voterCount={voters.length}
              userName={(uid) => userName(uid)}
              onChanged={invalidate}
            />
          </TabsContent>
        </Tabs>
      ) : (
        <ControlRoom
          selectionId={id}
          selection={selection}
          rounds={rounds}
          candidates={candidatesInScope}
          voters={voters}
          myRecusedIds={myRecusedIds}
          isOwner={isOwner}
          isCurrentUserVoter={isCurrentUserVoter}
          currentUserId={user?.id}
          userName={(uid) => userName(uid)}
          targetShortlist={shortlistCount}
          targetFinal={finalCount}
          suggestedMaxPicks={suggestedMaxPicks}
          revealedShortlistIds={revealedShortlistIds}
          onManage={() => setMode('manage')}
          onChanged={invalidate}
        />
      )}

      {showDelete && (
        <DeleteConfirmationDialog
          open={showDelete}
          onOpenChange={setShowDelete}
          onConfirm={() => deleteMutation.mutate()}
          entityName={selection.title}
          entityType="selection"
          isDeleting={deleteMutation.isPending}
        />
      )}
    </div>
  );
}

function currentExclusions(selection: Selection): string[] {
  const ids = (selection.scope as { exclude_happening_ids?: unknown })?.exclude_happening_ids;
  return Array.isArray(ids) ? (ids as string[]) : [];
}

function ScopeTab({
  selection,
  candidates,
  voterCount,
  isOwner,
  onChanged,
}: {
  selection: Selection;
  candidates: CandidateHappening[];
  voterCount: number;
  isOwner: boolean;
  onChanged: () => void;
}) {
  const inScope = candidates.filter((c) => !c.excluded);
  const excludedList = candidates.filter((c) => c.excluded);
  const scopeSize = inScope.length;

  // Plan: final list size + shortlist size, both %/absolute, live-resolved.
  const [target, setTarget] = useState<SelectionTarget>(() => getSelectionTarget(selection));
  useEffect(() => {
    setTarget(getSelectionTarget(selection));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selection.id]);

  const finalCount = resolveTargetField(scopeSize, target.final);
  const shortlistCount = resolveTargetField(scopeSize, target.shortlist);
  const suggestedShortlist = suggestShortlistCount(finalCount);
  const maxPicks = suggestMaxPicks(shortlistCount, voterCount);

  const savePlan = useMutation({
    mutationFn: () =>
      updateSelection(selection.id, { settings: { ...selection.settings, target } }),
    onSuccess: onChanged,
  });

  const setExclusions = useMutation({
    mutationFn: (ids: string[]) =>
      updateSelection(selection.id, {
        scope: { ...selection.scope, exclude_happening_ids: ids },
      }),
    onSuccess: onChanged,
  });
  const remove = (hid: string) =>
    setExclusions.mutate(Array.from(new Set([...currentExclusions(selection), hid])));
  const restore = (hid: string) =>
    setExclusions.mutate(currentExclusions(selection).filter((x) => x !== hid));

  const renderArtists = (c: CandidateHappening) =>
    c.artists.length > 0 ? c.artists.map((a) => a.name).join(', ') : '—';

  return (
    <div className="space-y-4 pt-4">
      <Card>
        <CardHeader className="pb-2">
          <CardDescription>Plan</CardDescription>
          <CardTitle className="text-base">
            {finalCount} final · {shortlistCount} shortlist · ~{voterCount > 0 ? maxPicks : '—'} picks/voter
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <TargetInput
            label="Final list size"
            explanation="How many shows end up in the published result."
            field={target.final}
            onChange={(final) => setTarget((t) => ({ ...t, final }))}
            scopeSize={scopeSize}
            disabled={!isOwner}
          />
          <TargetInput
            label="Shortlist size (round 1 output)"
            explanation="The set voters allocate points across in round 2. Bigger than the final so round 2 can narrow it down."
            field={target.shortlist}
            onChange={(shortlist) => setTarget((t) => ({ ...t, shortlist }))}
            scopeSize={scopeSize}
            suggestionCount={suggestedShortlist}
            disabled={!isOwner}
          />
          <p className="text-xs text-muted-foreground">
            Suggested max picks per voter in round 1:{' '}
            <span className="font-medium text-foreground">{voterCount > 0 ? maxPicks : '—'}</span> — each
            voter nominates up to this many (assumes ~1.6× overlap; editable when you open the round).
            {voterCount === 0 && ' Add voters to size this.'}
            {shortlistCount > 0 && shortlistCount <= finalCount && (
              <span className="text-destructive">
                {' '}Shortlist should be larger than the final list so round 2 has room to narrow.
              </span>
            )}
          </p>
          {isOwner && (
            <Button size="sm" onClick={() => savePlan.mutate()} disabled={savePlan.isPending}>
              {savePlan.isPending ? 'Saving…' : 'Save plan'}
            </Button>
          )}
        </CardContent>
      </Card>

      <p className="text-sm text-muted-foreground">
        {inScope.length} in scope
        {excludedList.length > 0 ? ` · ${excludedList.length} excluded` : ''}
      </p>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Artists</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>When</TableHead>
              <TableHead>Status</TableHead>
              {isOwner && <TableHead className="w-12" />}
            </TableRow>
          </TableHeader>
          <TableBody>
            {inScope.map((c) => (
              <TableRow key={c.id}>
                <TableCell className="font-medium">{c.title}</TableCell>
                <TableCell className="text-muted-foreground">{renderArtists(c)}</TableCell>
                <TableCell className="text-muted-foreground">{c.type}</TableCell>
                <TableCell className="text-muted-foreground">
                  {c.start_time ? new Date(c.start_time).toLocaleDateString() : '—'}
                </TableCell>
                <TableCell className="text-muted-foreground">{c.status}</TableCell>
                {isOwner && (
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                      title="Remove from scope"
                      onClick={() => remove(c.id)}
                      disabled={setExclusions.isPending}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
            {inScope.length === 0 && (
              <TableRow>
                <TableCell colSpan={isOwner ? 6 : 5} className="text-sm text-muted-foreground">
                  Nothing matches this scope.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {excludedList.length > 0 && (
        <details className="rounded-md border p-3">
          <summary className="cursor-pointer text-sm font-medium">
            Excluded from scope ({excludedList.length})
          </summary>
          <div className="mt-2 divide-y">
            {excludedList.map((c) => (
              <div key={c.id} className="flex items-center gap-3 py-2">
                <span className="font-medium">{c.title}</span>
                <span className="text-sm text-muted-foreground">{renderArtists(c)}</span>
                {isOwner && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="ml-auto"
                    onClick={() => restore(c.id)}
                    disabled={setExclusions.isPending}
                  >
                    Restore
                  </Button>
                )}
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

function VotersTab({
  id,
  accountUsers,
  currentVoterIds,
  isOwner,
  onSaved,
}: {
  id: string;
  accountUsers: User[];
  currentVoterIds: string[];
  isOwner: boolean;
  onSaved: () => void;
}) {
  const [selected, setSelected] = useState<Set<string>>(new Set(currentVoterIds));

  // Re-sync when the roster loads/changes upstream.
  useEffect(() => {
    setSelected(new Set(currentVoterIds));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentVoterIds.join(',')]);

  const dirty = useMemo(() => {
    const a = new Set(currentVoterIds);
    if (a.size !== selected.size) return true;
    for (const x of selected) if (!a.has(x)) return true;
    return false;
  }, [currentVoterIds, selected]);

  const saveMutation = useMutation({
    mutationFn: () => setVoters(id, Array.from(selected)),
    onSuccess: onSaved,
  });

  const toggle = (uid: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid);
      else next.add(uid);
      return next;
    });
  };

  return (
    <div className="space-y-4 pt-4">
      <p className="text-sm text-muted-foreground">
        The roster should be fixed before round 1 opens.
      </p>
      <div className="rounded-md border divide-y">
        {accountUsers.map((u) => (
          <label key={u.id} className="flex items-center gap-3 px-4 py-3">
            <input
              type="checkbox"
              checked={selected.has(u.id)}
              onChange={() => toggle(u.id)}
              disabled={!isOwner}
            />
            <span className="font-medium">{u.name}</span>
            <span className="text-sm text-muted-foreground">{u.email}</span>
            <span className="ml-auto text-xs capitalize text-muted-foreground">{u.role}</span>
          </label>
        ))}
        {accountUsers.length === 0 && (
          <p className="px-4 py-3 text-sm text-muted-foreground">No users in this account.</p>
        )}
      </div>
      {isOwner && (
        <Button onClick={() => saveMutation.mutate()} disabled={!dirty || saveMutation.isPending}>
          {saveMutation.isPending ? 'Saving…' : 'Save roster'}
        </Button>
      )}
    </div>
  );
}

function RecusalsTab({
  id,
  recusals,
  voters,
  candidates,
  isOwner,
  userName,
  happeningTitle,
  onChanged,
}: {
  id: string;
  recusals: import('@littlebooker/dal').SelectionRecusal[];
  voters: import('@littlebooker/dal').SelectionVoter[];
  candidates: CandidateHappening[];
  isOwner: boolean;
  userName: (uid: string | null) => string;
  happeningTitle: (hid: string) => string;
  onChanged: () => void;
}) {
  const [voterId, setVoterId] = useState('');
  const [happeningId, setHappeningId] = useState('');
  const [reason, setReason] = useState('');

  const addMutation = useMutation({
    mutationFn: () => addRecusal(id, { user_id: voterId, happening_id: happeningId, reason: reason || null }),
    onSuccess: () => {
      setVoterId('');
      setHappeningId('');
      setReason('');
      onChanged();
    },
  });

  const removeMutation = useMutation({
    mutationFn: (recusalId: string) => removeRecusal(id, recusalId),
    onSuccess: onChanged,
  });

  return (
    <div className="space-y-4 pt-4">
      <p className="text-sm text-muted-foreground">
        A voter is recused from happenings they performed on or are too close to. Their budget is
        unchanged — shares are normalised at tally.
      </p>

      {isOwner && (
        <div className="grid grid-cols-1 gap-3 rounded-md border p-3 md:grid-cols-4">
          <div>
            <label className="text-xs font-medium">Voter</label>
            <select className={inputClass} value={voterId} onChange={(e) => setVoterId(e.target.value)}>
              <option value="">Select…</option>
              {voters.map((v) => (
                <option key={v.user_id} value={v.user_id}>
                  {userName(v.user_id)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium">Happening</label>
            <select
              className={inputClass}
              value={happeningId}
              onChange={(e) => setHappeningId(e.target.value)}
            >
              <option value="">Select…</option>
              {candidates.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.title}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium">Reason</label>
            <input className={inputClass} value={reason} onChange={(e) => setReason(e.target.value)} />
          </div>
          <div className="flex items-end">
            <Button
              onClick={() => addMutation.mutate()}
              disabled={!voterId || !happeningId || addMutation.isPending}
            >
              Add recusal
            </Button>
          </div>
        </div>
      )}

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Voter</TableHead>
              <TableHead>Happening</TableHead>
              <TableHead>Reason</TableHead>
              {isOwner && <TableHead className="w-12" />}
            </TableRow>
          </TableHeader>
          <TableBody>
            {recusals.map((r) => (
              <TableRow key={r.id}>
                <TableCell className="font-medium">{userName(r.user_id)}</TableCell>
                <TableCell className="text-muted-foreground">{happeningTitle(r.happening_id)}</TableCell>
                <TableCell className="text-muted-foreground">{r.reason ?? '—'}</TableCell>
                {isOwner && (
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                      onClick={() => removeMutation.mutate(r.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
            {recusals.length === 0 && (
              <TableRow>
                <TableCell colSpan={isOwner ? 4 : 3} className="text-sm text-muted-foreground">
                  No recusals.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
